import { Component, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { SelectOptionDirective, SelectOptionType, SingleSelectComponent } from '@examdojo/core/select';
import { merge } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PAYOUT_REQUEST_STATUS_LABELS, PayoutRequestStatus } from '../payout-request/payout-request.model';
import { PaymentStatusBadgeComponent } from './payment-status-badge.component';

export interface StatusFilterOption {
  value: PayoutRequestStatus | 'ALL';
  label: string;
}

@Component({
  selector: 'dojo-payment-request-status-filter',
  template: `
    <div class="flex items-center gap-2">
      <span class="text-sm font-medium text-gray-700">Status:</span>
      <dojo-single-select
        [formCtrl]="statusFilterCtrl"
        [options]="statusOptions"
        [optionValue]="'value'"
        [optionLabel]="'label'"
        [compact]="true"
        [size]="'xl'"
        class="w-[220px]"
        placeholder="All statuses"
      >
        <ng-template [dojoSelectOption]="selectOptionType" let-option>
          <dojo-payment-status-badge [status]="option.value" [config]="{ size: 'sm', showIcon: true }" />
        </ng-template>
      </dojo-single-select>
    </div>
  `,
  imports: [SingleSelectComponent, SelectOptionDirective, PaymentStatusBadgeComponent],
})
export class PaymentRequestStatusFilterComponent {
  constructor() {
    merge(
      this.statusFilterCtrl.valueChanges.pipe(
        tap((value) => {
          this.statusFilterChanged.emit(value);
        }),
      ),

      toObservable(this.statusFilter).pipe(
        tap((value) => {
          this.statusFilterCtrl.setValue(value);
        }),
      ),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  readonly statusFilter = input<PayoutRequestStatus | 'ALL'>('WAITING_FOR_APPROVAL');

  readonly statusFilterCtrl = new FormControl<PayoutRequestStatus | 'ALL'>(this.statusFilter(), { nonNullable: true });

  readonly statusFilterChanged = output<PayoutRequestStatus | 'ALL'>();

  // Type variable for template type safety
  readonly selectOptionType!: SelectOptionType<StatusFilterOption>;

  readonly statusOptions: StatusFilterOption[] = [
    { value: 'ALL', label: 'All Statuses' },
    { value: 'DRAFT', label: PAYOUT_REQUEST_STATUS_LABELS.DRAFT },
    { value: 'WAITING_FOR_APPROVAL', label: PAYOUT_REQUEST_STATUS_LABELS.WAITING_FOR_APPROVAL },
    { value: 'APPROVED', label: PAYOUT_REQUEST_STATUS_LABELS.APPROVED },
    { value: 'REJECTED', label: PAYOUT_REQUEST_STATUS_LABELS.REJECTED },
    { value: 'PAYMENT_WAITING_FOR_APPROVAL', label: PAYOUT_REQUEST_STATUS_LABELS.PAYMENT_WAITING_FOR_APPROVAL },
    { value: 'PAYMENT_IN_PROGRESS', label: PAYOUT_REQUEST_STATUS_LABELS.PAYMENT_IN_PROGRESS },
    { value: 'PAYMENT_SUCCEEDED', label: PAYOUT_REQUEST_STATUS_LABELS.PAYMENT_SUCCEEDED },
    { value: 'PAYMENT_FAILED', label: PAYOUT_REQUEST_STATUS_LABELS.PAYMENT_FAILED },
  ];
}
